import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import StaffLayout from "@/components/StaffLayout";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { AlertTriangle, Clock, Loader2, RefreshCw, ChevronRight } from "lucide-react";

const PRIORITY_META = {
  urgent: "bg-red-500/20 text-red-300",
  high: "bg-orange-500/20 text-orange-300",
  normal: "bg-white/10 text-gray-300",
  low: "bg-white/5 text-gray-400",
};
const STATUS_META = {
  open: "bg-brand-orange-500/20 text-brand-orange-300",
  in_progress: "bg-amber-500/20 text-amber-300",
  waiting_user: "bg-cyan-500/20 text-cyan-300",
  resolved: "bg-emerald-500/20 text-emerald-300",
  closed: "bg-gray-500/20 text-gray-400",
};

export default function StaffSoporte() {
  const { user } = useAuth();
  const isSuper = user?.roles?.includes("superadmin_dcc");
  const [items, setItems] = useState([]);
  const [metrics, setMetrics] = useState(null);
  const [filters, setFilters] = useState({ status: "", priority: "", assigned_to: "", sla_breach: false });
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([k, v]) => { if (v) params.set(k, v); });
      const [r, m] = await Promise.all([
        api.get(`/staff/tickets?${params.toString()}`),
        api.get("/admin/tickets/metrics"),
      ]);
      setItems(r.data.items || []);
      setMetrics(m.data);
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [JSON.stringify(filters)]);

  return (
    <StaffLayout user={user} superadmin={isSuper}>
      <div className="mb-6 flex items-center justify-between gap-3 flex-wrap">
        <div>
          <p className="text-xs uppercase tracking-widest text-[#F26A21] font-bold">Soporte</p>
          <h1 className="mt-1 text-3xl font-bold text-white font-display">Tickets</h1>
          <p className="mt-1 text-sm text-gray-400">Cola operativa · SLA por prioridad · self-assign.</p>
        </div>
        <button onClick={load} data-testid="staff-tickets-refresh"
                className="inline-flex items-center gap-1 text-xs text-gray-300 hover:text-white">
          <RefreshCw className="w-3 h-3" /> Actualizar
        </button>
      </div>

      {metrics && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6" data-testid="tickets-metrics">
          <Metric label="Totales" value={metrics.total} />
          <Metric label="SLA compliance" value={`${metrics.sla_compliance_pct}%`}
                  highlight={metrics.sla_compliance_pct < 90 ? "warn" : "ok"} />
          <Metric label="Breach" value={metrics.sla_breached} highlight={metrics.sla_breached > 0 ? "warn" : "ok"} />
          <Metric label="Respuesta prom." value={`${metrics.avg_first_response_hours}h`} />
        </div>
      )}

      <div className="flex flex-wrap gap-2 mb-4" data-testid="tickets-filters">
        <select value={filters.status} onChange={(e) => setFilters(f => ({ ...f, status: e.target.value }))}
                data-testid="tickets-filter-status"
                className="rounded-md bg-white/5 border border-white/10 px-3 py-1.5 text-xs text-white">
          <option value="">Todos los estados</option>
          <option value="open">Abierto</option>
          <option value="in_progress">En atención</option>
          <option value="waiting_user">Esperando alumno</option>
          <option value="resolved">Resuelto</option>
          <option value="closed">Cerrado</option>
        </select>
        <select value={filters.priority} onChange={(e) => setFilters(f => ({ ...f, priority: e.target.value }))}
                data-testid="tickets-filter-priority"
                className="rounded-md bg-white/5 border border-white/10 px-3 py-1.5 text-xs text-white">
          <option value="">Todas las prioridades</option>
          <option value="urgent">Urgente</option>
          <option value="high">Alta</option>
          <option value="normal">Normal</option>
          <option value="low">Baja</option>
        </select>
        <select value={filters.assigned_to} onChange={(e) => setFilters(f => ({ ...f, assigned_to: e.target.value }))}
                data-testid="tickets-filter-assign"
                className="rounded-md bg-white/5 border border-white/10 px-3 py-1.5 text-xs text-white">
          <option value="">Cualquier asignación</option>
          <option value="unassigned">Sin asignar</option>
          <option value={user?.id || ""}>Asignados a mí</option>
        </select>
        <label className="inline-flex items-center gap-1 text-xs text-white">
          <input type="checkbox" checked={filters.sla_breach}
                 onChange={(e) => setFilters(f => ({ ...f, sla_breach: e.target.checked }))}
                 data-testid="tickets-filter-breach"
                 className="accent-[#F26A21]" />
          Solo SLA breach
        </label>
      </div>

      {loading ? (
        <div className="text-sm text-gray-400 inline-flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Cargando…</div>
      ) : items.length === 0 ? (
        <div className="rounded-2xl border border-white/10 bg-white/5 p-8 text-center text-sm text-gray-400" data-testid="tickets-empty">
          Sin tickets con los filtros seleccionados.
        </div>
      ) : (
        <div className="rounded-2xl border border-white/10 bg-white/5 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-white/[0.03]">
              <tr className="text-left text-[10px] uppercase tracking-widest text-gray-400">
                <th className="px-3 py-2.5">Ticket</th>
                <th className="px-3 py-2.5">Alumno</th>
                <th className="px-3 py-2.5">Asunto</th>
                <th className="px-3 py-2.5">Cat</th>
                <th className="px-3 py-2.5">Prio</th>
                <th className="px-3 py-2.5">Estado</th>
                <th className="px-3 py-2.5">Asignado</th>
                <th className="px-3 py-2.5">Abierto</th>
                <th className="px-3 py-2.5"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {items.map(t => (
                <tr key={t.id} data-testid={`tickets-row-${t.id}`}>
                  <td className="px-3 py-2 font-mono text-[10px] text-gray-300">{t.ticket_number}</td>
                  <td className="px-3 py-2">
                    <p className="text-white font-semibold text-xs">{t.user_name}</p>
                    <p className="text-[10px] text-gray-500">{t.user_email}</p>
                  </td>
                  <td className="px-3 py-2 text-xs text-white max-w-xs truncate">{t.subject}</td>
                  <td className="px-3 py-2 text-[11px] text-gray-300">{t.category}</td>
                  <td className="px-3 py-2">
                    <span className={`text-[10px] uppercase tracking-widest font-bold px-1.5 rounded ${PRIORITY_META[t.priority] || ""}`}>{t.priority}</span>
                  </td>
                  <td className="px-3 py-2">
                    <span className={`text-[10px] uppercase tracking-widest font-bold px-1.5 rounded ${STATUS_META[t.status] || ""}`}>{t.status}</span>
                    {t.sla_breached && (
                      <span className="ml-1 inline-flex items-center gap-1 text-[10px] text-red-300 font-bold" data-testid={`ticket-sla-breach-${t.id}`}>
                        <AlertTriangle className="w-3 h-3" /> SLA
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-[11px] text-gray-300">{t.assigned_to_name || "—"}</td>
                  <td className="px-3 py-2 text-[10px] text-gray-500">{new Date(t.created_at).toLocaleDateString("es-MX")}</td>
                  <td className="px-3 py-2 text-right">
                    <Link to={`/app/staff/soporte/${t.id}`} data-testid={`tickets-open-${t.id}`}
                          className="inline-flex items-center gap-1 text-xs text-[#F26A21] hover:text-white">
                      Abrir <ChevronRight className="w-3 h-3" />
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </StaffLayout>
  );
}

function Metric({ label, value, highlight }) {
  const cls = highlight === "warn" ? "border-red-500/40 bg-red-500/10" :
              highlight === "ok" ? "border-emerald-500/30 bg-emerald-500/5" :
              "border-white/10 bg-white/5";
  return (
    <div className={`rounded-2xl border ${cls} p-3`} data-testid={`ticket-metric-${label}`}>
      <p className="text-[10px] uppercase tracking-widest text-gray-400 font-semibold">{label}</p>
      <p className="mt-1 text-2xl font-bold text-white">{value}</p>
    </div>
  );
}
